import React from 'react';

function TermsOfUse() {
  return (
    <section className="page-content">
      <div className="container">
        <h1 className="page-title">Terms of Service</h1>
        <div className="page-body legal-content">

          {/* Overview */}
          <div className="info-box">
            <p><strong>App Name:</strong> ForTuber</p>
            <p><strong>Developer:</strong> DIDI Connect</p>
            <p><strong>Company:</strong> DIDI</p>
          </div>

          <p>
            These Terms of Service govern your use of ForTuber. Please read these terms carefully before using our service.
          </p>

          <p>
            These Terms of Service apply to all services provided by us through our mobile app, website, and mobile website (collectively, the "Service").
          </p>

          <p>
            ForTuber is an AI-powered app for YouTube creators that provides multi-language translation of channel description, video metadata (titles, descriptions), and video subtitles/captions
            to help creators reach global audiences.
          </p>

          <p>
            By using the Service, you agree to be bound by these Terms of Service. If you do not agree to these terms, you may not use the Service.
          </p>

          {/* YouTube API Services Compliance */}
          <div className="notice-box important">
            <p>
              <strong>YouTube API Services:</strong> ForTuber uses YouTube API Services. By using ForTuber, you also agree to be bound by the{' '}
              <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">YouTube Terms of Service</a>.
            </p>
          </div>

          <h2>Article 1 (Purpose)</h2>
          <p>
            These Terms of Service govern the rights, obligations, and responsibilities between DIDI (hereinafter "the Company")
            and Members (as defined below) regarding the use of all services provided through ForTuber.
          </p>

          <h2>Article 2 (Definitions)</h2>
          <p>The definitions of terms used in these Terms of Service are as follows:</p>
          <ol>
            <li>"Service" means all services provided by the Company through ForTuber, regardless of the type of wired or wireless terminal used to access them.</li>
            <li>"Member" means any user who has entered into a service agreement with the Company and uses the services provided by the Company.</li>
            <li>"Google Account" means a member account registered through Google authentication via Firebase.</li>
            <li>"Company" means DIDI, the entity operating and providing the Service.</li>
            <li>"YouTube Data" means any data accessed through the YouTube API Services, including but not limited to channel description, video metadata (titles, descriptions), and video subtitles/captions.</li>
            <li>"Credit" means the virtual currency used within the Service that can be purchased through in-app purchases to access paid features.</li>
          </ol>

          <h2>Article 3 (Disclosure and Amendment of Terms)</h2>
          <ol>
            <li>The Company shall post the contents of these Terms, company name, representative name, contact information, and privacy officer information on the Service pages so that Members can easily access them.</li>
            <li>The Company may amend these Terms to the extent that such amendments do not violate applicable laws and regulations.</li>
            <li>When the Company amends these Terms, it shall notify Members at least 7 days before the effective date through in-app notifications and the website. For significant changes that affect Members' rights, the Company shall provide at least 30 days' prior notice.</li>
            <li>If a Member does not agree to the amended Terms, the Member may discontinue using the Service and request account deletion.</li>
            <li>Matters not specified in these Terms shall be governed by applicable laws, YouTube Terms of Service, and general commercial practices.</li>
          </ol>

          <h2>Article 4 (Provision and Modification of Service)</h2>
          <ol>
            <li>The Company provides the following services to Members:
              <ol>
                <li><strong>Multi-Language Translation:</strong> Automatic translation of channel description, video metadata (titles, descriptions), and video subtitles/captions into multiple languages using AI technology</li>
                <li>Any other services that the Company may develop or provide to Members</li>
              </ol>
            </li>
            <li>This Service utilizes YouTube content through the YouTube API Services and complies with:
              <ol>
                <li><a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">YouTube Terms of Service</a></li>
                <li><a href="https://developers.google.com/youtube/terms/api-services-terms-of-service" target="_blank" rel="noopener noreferrer">YouTube API Services Terms of Service</a></li>
                <li><a href="http://www.google.com/policies/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></li>
              </ol>
            </li>
            <li>The Company does not grant Members the right to use the Company's or Service's trade name, trademark, service mark, logo, domain name, or other distinctive brand features without a separate written agreement.</li>
            <li>The Service is provided 24 hours a day, 365 days a year, in principle. However, the Company may temporarily suspend the Service due to maintenance, replacement, or failure of information and communication facilities, communication interruption, or other significant operational reasons. In such cases, the Company will notify Members through various methods.</li>
            <li>The Company may modify all or part of the Service for operational or technical reasons when there is a significant need. If there are changes to the Service content, usage methods, or usage hours, the Company must post the reasons for the changes, the changed Service content, and the provision date before the changes take effect.</li>
          </ol>

          <h2>Article 5 (YouTube API Services Usage)</h2>
          <ol>
            <li>By using the Service, you agree to be bound by the <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">YouTube Terms of Service</a> (https://www.youtube.com/t/terms).</li>
            <li>ForTuber accesses and uses YouTube Data through the YouTube API Services to provide its core features.</li>
            <li>Members authorize ForTuber to access their YouTube channel data, including:
              <ol>
                <li>Channel description</li>
                <li>Video titles, descriptions, and metadata</li>
                <li>Video subtitles/captions</li>
              </ol>
            </li>
            <li>ForTuber uses YouTube Data solely for the purposes described in these Terms and our Privacy Policy.</li>
            <li>Members can revoke ForTuber's access to their YouTube Data at any time through:
              <ol>
                <li>The app&apos;s Settings menu (e.g., Delete Account)</li>
                <li>Google account: <a href="https://myaccount.google.com/connections?filters=3,4&hl=en" target="_blank" rel="noopener noreferrer">Third-party apps with account access (Connections)</a> — find ForTuber/DIDI and remove access</li>
              </ol>
            </li>
            <li>YouTube channel authorization tokens (Access Token and Refresh Token) are encrypted and stored only on the Member's device. These tokens are never transmitted to external servers.</li>
            <li>Members are responsible for ensuring that their use of the Service complies with YouTube's Community Guidelines and Terms of Service.</li>
          </ol>

          <h2>Article 6 (In-App Purchases and Credits)</h2>
          <ol>
            <li>The Company provides in-app purchase services through which Members can purchase virtual currency called "Credits" to use paid features within the Service.</li>
            <li>Credits can be purchased through in-app purchases via Apple App Store or Google Play Store. The price, quantity, and terms of Credit packages are specified on the Service screen.</li>
            <li>Credits are virtual currency used within the Service and can be used for:
              <ol>
                <li>Multi-language translation of channel description, video metadata, and video subtitles/captions</li>
                <li>Other paid features as specified in the Service</li>
              </ol>
            </li>
            <li>Purchased Credits are non-transferable and cannot be exchanged for cash or other forms of currency.</li>
            <li>Credits have no expiration date while the Member's account remains active. However, upon account deletion, all remaining Credits will be forfeited.</li>
            <li>Refunds for purchased Credits are subject to applicable laws and the policies of the respective app store (Apple App Store, Google Play Store). Due to the nature of digital content, refunds may be limited once Credits have been used.</li>
            <li>The Company may change the price of Credit packages or the Credit cost of features within the Service. Such changes will be announced in advance through the Service, and previously purchased Credits will remain valid at their original value.</li>
            <li>Members are responsible for all purchases made through their account. The Company is not responsible for unauthorized purchases resulting from the Member's failure to secure their account or device.</li>
          </ol>

          <h2>Article 7 (Suspension of Service)</h2>
          <ol>
            <li>The Company may temporarily suspend the Service in cases of maintenance, replacement, or failure of information and communication facilities, communication interruption, etc.</li>
            <li>The Company may modify, suspend, or change all or part of the free services at its discretion for policy or operational reasons, and unless otherwise required by applicable laws, the Company shall not provide separate compensation to Members.</li>
            <li>The Service may also be affected by changes to the YouTube API Services. The Company will notify Members of any significant changes as soon as practicable.</li>
          </ol>

          <h2>Article 8 (Member Registration)</h2>
          <ol>
            <li>Member registration is available through Google Account authentication (via Firebase) only.</li>
            <li>By registering, Members agree to provide accurate and complete information.</li>
            <li>The Company shall register as a Member any person who applies for membership, except in the following cases:
              <ol>
                <li>When the registration contains false information, omissions, or errors</li>
                <li>When registration as a Member is deemed to significantly hinder the Company's technical operations</li>
                <li>When the applicant has previously been terminated for violating these Terms</li>
              </ol>
            </li>
            <li>The membership contract is established when the Company's approval reaches the Member.</li>
          </ol>

          <h2>Article 9 (Member Withdrawal and Account Deletion)</h2>
          <ol>
            <li>Members may request withdrawal from the Company at any time, and the Company shall process the withdrawal immediately.</li>
            <li>Account deletion can be requested through:
              <ol>
                <li><strong>App Settings:</strong> Settings → Delete Account</li>
                <li><strong>Email:</strong> cddchingudeul@gmail.com</li>
              </ol>
            </li>
            <li>Upon account deletion:
              <ol>
                <li>All personal information will be deleted in accordance with our Privacy Policy</li>
                <li>Access to YouTube Data through ForTuber will be revoked</li>
                <li>All remaining Credits will be forfeited and cannot be refunded</li>
              </ol>
            </li>
            <li>The Company may restrict or suspend a Member's qualification in the following cases:
              <ol>
                <li>When false information is registered during the registration application</li>
                <li>When a Member violates YouTube's Terms of Service or Community Guidelines while using the Service</li>
                <li>When a Member interferes with another person's use of the Service or misappropriates their information</li>
              </ol>
            </li>
          </ol>

          <h2>Article 10 (Notification to Members)</h2>
          <ol>
            <li>When the Company notifies Members, it may do so through email addresses, push notifications, or in-app notifications, unless otherwise specified in these Terms.</li>
            <li>The Company may substitute the notification in paragraph 1 by posting a notice on the app or website for 7 days or more for notifications to all Members.</li>
          </ol>

          <h2>Article 11 (Company's Obligations)</h2>
          <ol>
            <li>The Company shall not engage in acts prohibited by applicable laws or these Terms or contrary to public order and morals, and shall make its best efforts to provide the Service continuously and stably.</li>
            <li>The Company shall establish security systems to protect personal information so that Members can use the Service safely, and shall publish and comply with its Privacy Policy.</li>
            <li>The Company shall comply with the YouTube API Services Terms of Service and Google API Services User Data Policy in handling YouTube Data.</li>
            <li>The Company shall have the necessary personnel and systems in place to properly handle complaints or requests for damage relief from users related to the use of the Service.</li>
          </ol>

          <h2>Article 12 (Member's Obligations)</h2>
          <ol>
            <li>Members shall comply with YouTube's Terms of Service and Community Guidelines when using the Service.</li>
            <li>Members shall not engage in the following acts:
              <ol>
                <li>Registering false information when applying or making changes</li>
                <li>Misappropriating another person's information or YouTube account</li>
                <li>Using the Service to spam, harass, or violate the rights of others</li>
                <li>Infringing on the intellectual property rights, including copyrights, of the Company, YouTube, or other third parties</li>
                <li>Using the Service for commercial purposes beyond the intended use without the Company's consent</li>
                <li>Attempting to access YouTube Data beyond the authorized scope</li>
                <li>Reverse engineering, decompiling, or attempting to extract the source code of the Service</li>
                <li>Other illegal or improper acts</li>
              </ol>
            </li>
            <li>Members are solely responsible for the translated content they generate using the Service.</li>
          </ol>

          <h2>Article 13 (Provision of Information and Posting of Advertisements)</h2>
          <ol>
            <li>The Company may provide various information deemed necessary to Members during their use of the Service through notices, push notifications, or email. Members may opt out of non-essential communications at any time.</li>
            <li>The Company may display advertisements within the Service through Google AdMob. Members can adjust ad personalization settings through their device settings.</li>
          </ol>

          <h2>Article 14 (Content and Intellectual Property)</h2>
          <ol>
            <li>Copyrights and intellectual property rights to the Service (excluding YouTube content) belong to the Company.</li>
            <li>YouTube content accessed through the Service remains the property of the respective content creators and is subject to YouTube's Terms of Service.</li>
            <li>Translations generated by the Service (including channel description, video metadata, and subtitles/captions) are created for the Member's use in connection with their YouTube channel. The Company does not claim ownership of such generated content.</li>
            <li>Members shall not reproduce, transmit, publish, distribute, or otherwise use information to which the Company has intellectual property rights without the prior consent of the Company.</li>
          </ol>

          <h2>Article 15 (Termination of Contract)</h2>
          <ol>
            <li>Members may request termination of the service agreement at any time through the account deletion feature (Settings → Delete Account) or by contacting cddchingudeul@gmail.com.</li>
            <li>When a Member terminates the contract, all of the Member's data shall be deleted in accordance with applicable laws and our Privacy Policy.</li>
            <li>Upon termination, the Member's authorization for ForTuber to access their YouTube Data will be revoked.</li>
          </ol>

          <h2>Article 16 (Restriction of Use)</h2>
          <ol>
            <li>The Company may restrict the use of the Service in stages, such as warnings, temporary suspension, or permanent suspension, when a Member violates the obligations under these Terms or interferes with the normal operation of the Service.</li>
            <li>The Company may also restrict use if a Member's actions violate YouTube's Terms of Service or may result in ForTuber's access to YouTube API Services being revoked.</li>
            <li>Members may file an objection to restrictions by contacting cddchingudeul@gmail.com. If the Company recognizes that the objection is justified, the Company shall resume the use of the Service.</li>
          </ol>

          <h2>Article 17 (Limitation of Liability)</h2>
          <ol>
            <li>The Company shall be exempt from liability for the provision of the Service in cases where the Service cannot be provided due to natural disasters or equivalent force majeure.</li>
            <li>The Company shall not be liable for obstacles to the use of the Service due to the Member's fault.</li>
            <li>The Company shall not be liable for any issues arising from changes to the YouTube API Services or YouTube's policies that are beyond the Company's control.</li>
            <li>The Company shall not be liable for the accuracy of translations generated by the Service. Members are responsible for reviewing and editing translated content before publishing.</li>
            <li>The Company shall not be liable for any consequences arising from a Member's violation of YouTube's Terms of Service or Community Guidelines.</li>
            <li>The Company shall not be liable for the use of free services unless otherwise required by applicable laws.</li>
          </ol>

          <h2>Article 18 (Interpretation of Terms)</h2>
          <p>
            Matters not specified in these Terms shall be determined by agreement between the Company and Members,
            and if no agreement is reached, they shall be governed by applicable laws, YouTube Terms of Service, or general commercial practices.
          </p>

          <h2>Article 19 (Governing Law and Jurisdiction)</h2>
          <ol>
            <li>These Terms shall be governed by and construed in accordance with applicable laws.</li>
            <li>Any disputes arising between the Company and Members shall be resolved through good faith negotiations. If such negotiations fail, disputes shall be resolved in accordance with applicable laws and regulations.</li>
          </ol>

          <h2>Article 20 (Contact Information)</h2>
          <p>For questions, complaints, or requests regarding these Terms or the Service, please contact:</p>
          <div className="contact-box">
            <p>
              Company: DIDI<br />
              Developer: DIDI Connect<br />
              App Name: ForTuber<br />
              Email: cddchingudeul@gmail.com
            </p>
          </div>

          <p className="effective-date">These Terms are effective as of January 19, 2026.</p>
        </div>
      </div>
    </section>
  );
}

export default TermsOfUse;
